import settings from 'src/config/settings'

import { PromotionSegment } from '../models/common'

/**
 * Wire-level configuration of service-promotions. Everything the UI has to agree
 * with the service about — and nothing the service can tell us itself — lives here.
 */

/** Empty when the service is not configured: the api then falls back to mocks. */
export const promotionsServiceBaseUrl = settings.httpsServices.promotions

export const PROMOTIONS_SEARCH_PATH = '/promotions/search'
export const PRODUCT_FOCUS_PATH = '/product-focus'

/**
 * Catalogue content of one product, addressed by sku in the path. One handle for
 * every detail-panel tab, because the catalogue answers them in one body.
 */
export const PRODUCT_CONTENT_PATH = '/product-content'

/**
 * Rows per page: the `take` of every search, and the divisor the pager turns the
 * service's `total` into a page count with. 200 is the service maximum for
 * `take` — asking for more is a 400, not a bigger page.
 *
 * The cut counts **list rows**, and `groupBy` decides what a row is, so the same
 * page size means 200 products in the grouped view and 200 SKUs in the flat one.
 */
export const PROMOTIONS_PAGE_SIZE = 200

/**
 * The service segment codes differ from the UI enum for two segments, so the
 * translation is explicit rather than a cast.
 */
export const PROMOTION_SEGMENT_CODES: Readonly<Record<PromotionSegment, string>> = {
  [PromotionSegment.BPS]: 'bps',
  [PromotionSegment.CPS]: 'cps',
  [PromotionSegment.Display]: 'displays',
  [PromotionSegment.Poly]: 'poly',
  [PromotionSegment.Other]: 'other_acc',
  [PromotionSegment.OPS]: 'ops',
  [PromotionSegment.HPS]: 'hps',
}

export const getTodayIsoDate = () => new Date().toISOString().slice(0, 10)

/**
 * The date the list is evaluated as of: the floor of the window on the way out,
 * and the "now" promotions are marked upcoming/active/ended against on the way
 * back (`PromotionsDataset.activeOn`), so both sides agree on it.
 *
 * It travels as `endsAfter`, not as `activeOn`. The list is "has not ended", not
 * "is active today" — FR-9 wants offers that have not started drawn dimmed with
 * an "Upcoming" badge, and `activeOn` would drop them before they were ever
 * sent. The service keeps `activeOn` as its fallback for a request carrying no
 * date at all; this client never sends such a request.
 *
 * Production always gets today — the dev override is stripped from non-dev builds
 * in `src/config/settings.ts`. The mock data source never asks here: its dataset
 * is written around its own date.
 */
export const getPromotionsActiveOnDate = () => settings.promotionsActiveOn || getTodayIsoDate()
